// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Trans } from "react-i18next/icu.macro";

import { BodyText, Box, FieldLabel, FlexBox, Icon, StatusText } from "@braze/beacon-core";
import type { ButtonLoadingState } from "@braze/beacon-hooks";
import { getColor } from "@braze/beacon-styling";

import useI18nNamespace from "@services/useI18nNamespace";

import { BrazeButton } from "@components/BrazeButton";
import { BrazeInput } from "@components/BrazeInput";

import { ErrorCode, getErrorMessage, useValidateEmail, useVerifyEmail } from "../../../../hooks";
import { WorkEmailInputField } from "../../WorkEmailInputField";
import { isAllDigits } from "../../utils";
import type { VerifyContactDetailsType } from "../types";
import { StyledBodyText } from "./EmailVerification.style";
import type { EmailVerificationProps } from "./types";

export const EmailVerification = ({
  emailVerified,
  onVerificationComplete,
  setShowSalesBanner,
}: EmailVerificationProps): React.ReactElement => {
  const { t, k } = useI18nNamespace("free-trial-registration");
  const { getFieldState, watch, control, setError, trigger } = useFormContext<VerifyContactDetailsType>();
  const SALES_BANNER_DELAY = 2000;
  const RESEND_BUTTON_DELAY = 30000;

  const email = watch("email");
  const [verificationEmailSent, setVerificationEmailSent] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>();
  const [shouldValidateVerificationCode, setShouldValidateVerificationCode] = useState<boolean>(false);
  const [maxSendAttemptsErrorMessage, setMaxSendAttemptsErrorMessage] = useState<string>();
  const [reachedMaxSendAttempts, setReachedMaxSendAttempts] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const resendVerificationEmailLabel = t("resend-verification-email-button-text", "Resend verification email");
  const verificationCodeLabel = t("email-verification-code-label", "Code");
  const verifyLabel = t("verify-email", "Verify");
  const sendVerificationEmailLabel = t("send-verification-email", "Send verification email");
  const clearErrorMessage = useCallback(() => setErrorMessage(""), [setErrorMessage]);
  const { mutateAsync: verifyEmail, isLoading: isVerifyEmailLoading } = useVerifyEmail({
    onError: (errorCode) => {
      const errorMessage = getErrorMessage(t, errorCode);
      setError("email", { message: errorMessage });
      if (errorCode === ErrorCode.ReachedMaxSendAttempts) {
        setMaxSendAttemptsErrorMessage(errorMessage);
        setReachedMaxSendAttempts(true);
      }
    },
    onSuccess: () => {
      setVerificationEmailSent(true);
    },
  });
  const { mutateAsync: validateEmail, isLoading: isValidateEmail } = useValidateEmail({
    onError: setErrorMessage,
    onSuccess: onVerificationComplete,
  });

  useEffect(() => {
    const timeout = setTimeout(
      () => setShowSalesBanner(reachedMaxSendAttempts),
      reachedMaxSendAttempts ? SALES_BANNER_DELAY : 0
    );
    return () => clearTimeout(timeout);
  }, [setShowSalesBanner, reachedMaxSendAttempts]);

  const getButtonLoadingState = (isLoading: boolean): ButtonLoadingState => (isLoading ? "loading" : "idle");

  const verifyEmailButtonRef = useRef();
  const validateEmailButtonRef = useRef();
  const resendButtonRef = useRef();

  const invalidVerificationCode = useMemo(() => {
    if (shouldValidateVerificationCode && (!isAllDigits(verificationCode) || verificationCode.length !== 6)) {
      return t("email-verification-code-status-text", "Please enter a valid 6-digit verification code");
    }
    return null;
  }, [t, shouldValidateVerificationCode, verificationCode]);

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const sendVerificationEmail = useCallback(async () => {
    setIsDisabled(true);
    await trigger("email");
    // Check if email is invalid
    if (!email || getFieldState("email").invalid) {
      return;
    }
    // API call to send verification email
    await verifyEmail({
      email,
    });

    // Set a 30-second timer to re-enable the button
    await delay(RESEND_BUTTON_DELAY);
    setIsDisabled(false);
  }, [email, verifyEmail, getFieldState, trigger]);

  const handleEmailVerificationCodeBlur = useCallback(() => {
    setShouldValidateVerificationCode(true);
  }, [setShouldValidateVerificationCode]);

  const verifyVerificationCode = useCallback(async () => {
    // API call to verify verification code
    await validateEmail({
      verification_code: verificationCode?.toString(),
    });
  }, [verificationCode, validateEmail]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        // check if the key is not being held down
        if (!event.repeat) {
          if (verificationEmailSent) {
            verifyVerificationCode();
          } else {
            sendVerificationEmail();
          }
        }
      }
    },
    [verificationEmailSent, verifyVerificationCode, sendVerificationEmail]
  );

  if (emailVerified) {
    return (
      <FieldLabel
        label={t("work-email-verified-label", "Work email")}
        htmlFor="workEmail"
        infoIconPopoverContent={t(
          "popover-content",
          "For the best trial experience, please use a company email address. Personal accounts and aliases are not allowed."
        )}
      >
        <FlexBox id="emailVerifiedSuccessStatus" gap="6px" alignItems="flex-start">
          <Icon name="check-circle" color={getColor("lightBlue", 500)} />
          <BodyText>{t("work-email-verified", "Verified")}</BodyText>
        </FlexBox>
      </FieldLabel>
    );
  } else if (verificationEmailSent) {
    return (
      <>
        <StyledBodyText id="emailVerificationCodeSentInfo">
          <Trans i18nKey={k("resend-verification-email-description")}>
            A verification code has been sent to <strong>{email}</strong>. If you have not received a code, please
            wait 30 seconds before requesting a new one.
          </Trans>
        </StyledBodyText>
        <Box mt="sm">
          <BrazeButton
            operatorLabel={resendVerificationEmailLabel}
            ref={resendButtonRef}
            id="resendVerificationEmailButton"
            name="resendVerificationEmailButton"
            variant="quaternary"
            intent="info"
            onClick={sendVerificationEmail}
            loadingState={getButtonLoadingState(isVerifyEmailLoading)}
            disabled={reachedMaxSendAttempts || isDisabled}
          >
            {resendVerificationEmailLabel}
          </BrazeButton>
          {reachedMaxSendAttempts && <StatusText status="danger">{maxSendAttemptsErrorMessage}</StatusText>}
        </Box>
        <FieldLabel
          data-cy="emailVerificationCodeField"
          label={verificationCodeLabel}
          htmlFor="emailVerificationCode"
          status="danger"
          statusText={invalidVerificationCode || errorMessage}
        >
          <BrazeInput
            operatorLabel={verificationCodeLabel}
            type="text"
            pattern="[0-9]*"
            id="emailVerificationCode"
            placeholder={t("email-six-digit-code-placeholder", "Enter 6-digit code")}
            fullWidth
            value={verificationCode ?? ""}
            onBlur={handleEmailVerificationCodeBlur}
            invalid={!!invalidVerificationCode || !!errorMessage}
            onChange={(e) => setVerificationCode(e.target.value)}
            onFocusCapture={clearErrorMessage}
            onKeyDown={handleKeyDown}
          />
        </FieldLabel>
        <BrazeButton
          operatorLabel={verifyLabel}
          ref={validateEmailButtonRef}
          name="verifyEmailVerificationCodeButton"
          id="verifyEmailVerificationCodeButton"
          size="full"
          disabled={!!invalidVerificationCode || !verificationCode}
          onClick={verifyVerificationCode}
          loadingState={getButtonLoadingState(isValidateEmail)}
        >
          {verifyLabel}
        </BrazeButton>
      </>
    );
  }

  return (
    <>
      <WorkEmailInputField
        label={t("work-email-label", "Work email")}
        name="email"
        control={control}
        onKeyDown={handleKeyDown}
        disabled
      />
      <BrazeButton
        operatorLabel={sendVerificationEmailLabel}
        ref={verifyEmailButtonRef}
        name="verifyEmailButton"
        id="verifyEmailButton"
        size="full"
        onClick={sendVerificationEmail}
        loadingState={getButtonLoadingState(isVerifyEmailLoading)}
      >
        {sendVerificationEmailLabel}
      </BrazeButton>
    </>
  );
};
